import React from "react";
import cn from "classnames";
import styles from "./ai-rag-chat-modal.module.scss";
import { SmallSpinner } from "@src/components/atoms/small-spinner/small-spinner";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import chatBotProfile from "@src/assets/svg/chat-bot-profile.svg";
import { ThinkingSection } from "./thinking-section";
import { ToolActivitySection } from "@src/components/molecules/tool-activity-section/tool-activity-section";
// import { SourcesSection } from "./sources-section";
import { splitForRendering } from "./stream-utils";
import DOMPurify from "dompurify";
import { Message } from "./use-ai-chat";
import userIconBlue from "@src/assets/svg/user-icon-blue.svg";
import thumbsDownOutlineIcon from "@src/assets/svg/thumbs-down-icon-outline-blue.svg";
import thumbsUpOutlineIcon from "@src/assets/svg/thumbs-up-icon-outline-blue.svg";
import thumbsDownRedIcon from "@src/assets/svg/thumbs-down-icon-solid-red.svg";
import thumbsUpGreenIcon from "@src/assets/svg/thumbs-up-icon-solid-green.svg";
import { useSendFeedback } from "./use-send-feedback";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useI18N } from "@src/language/language-context";
import copyNodeIcon from "@src/assets/svg/copy-node-icon.svg";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
// import { ExpandedSourcesContainer } from "./expanded-sources-container";

const cssClassName = "ai-rag-chat-modal";

const LocalImage = ({
  file,
  className,
  alt,
}: {
  file: File;
  className: string;
  alt: string;
}) => {
  const [src, setSrc] = React.useState<string>();

  React.useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return src ? <img src={src} className={className} alt={alt} /> : null;
};

interface ChatMessageProps {
  message: Message;
  namespace: string | null;
  isThinkingExpanded: boolean;
  isSourcesExpanded: boolean;
  onToggleThinking: () => void;
  onToggleSources: () => void;
  updateMessageFeedback: (messageId: string, useful: boolean) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  namespace,
  isThinkingExpanded,
  isSourcesExpanded,
  onToggleThinking,
  onToggleSources,
  updateMessageFeedback,
}) => {
  const i18n = useI18N();
  const { sendPositiveFeedback, sendNegativeFeedback } =
    useSendFeedback(namespace);

  const texts = {
    errorSendingFeedback: i18n("modules.aiRagChat.errorSendingFeedback"),
    thinkingBubbleText: i18n("modules.aiRagChat.thinkingBubbleText"),
  };

  const handleSendPositiveFeedback = async (e) => {
    e.preventDefault();
    const messageIdForApi = message.serverMessageId || message.id;
    const response = await sendPositiveFeedback(messageIdForApi);

    if (response?.status === 200) {
      updateMessageFeedback(message.id, true);
    } else {
      errorSnackbar(texts.errorSendingFeedback);
    }
  };

  const handleSendNegativeFeedback = async (e) => {
    e.preventDefault();
    const messageIdForApi = message.serverMessageId || message.id;
    const response = await sendNegativeFeedback(messageIdForApi);

    if (response?.status === 200) {
      updateMessageFeedback(message.id, false);
    } else {
      errorSnackbar(texts.errorSendingFeedback);
    }
  };

  const handleCopyAnswer = async () => {
    const { answer } = splitForRendering(
      message.content,
      message.isStreaming ?? false,
    );

    if (answer) {
      const sanitizedHtml = DOMPurify.sanitize(answer);

      // convert HTML to plain text
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = sanitizedHtml;
      const plainText = tempDiv.innerText || tempDiv.textContent || "";

      try {
        await navigator.clipboard.writeText(plainText);
      } catch (err) {
        console.error("Failed to copy:", err);
        errorSnackbar("Failed to copy answer to clipboard.");
      }
    }
  };

  const renderAssistantMessage = () => {
    const { thinking, answer } = splitForRendering(
      message.content,
      message.isStreaming ?? false,
    );
    const hasThinking = thinking.length > 0;

    const hasTools = !!message.tools && message.tools.length > 0;

    return (
      <>
        {message.isStreaming && thinking.length === 0 && hasTools && (
          <div
            className={cn(
              styles[`${cssClassName}__thinking`],
              styles[`${cssClassName}__thinking--with-tools`],
            )}
          >
            <div className={styles[`${cssClassName}__spinner-wrapper`]}>
              <SmallSpinner />
            </div>
            <p>{texts.thinkingBubbleText}</p>
          </div>
        )}

        {message.tools && message.tools.length > 0 && (
          <ToolActivitySection
            tools={message.tools}
            isStreaming={message.isStreaming ?? false}
          />
        )}

        {hasThinking && (
          <ThinkingSection
            thinking={thinking}
            isExpanded={isThinkingExpanded}
            onToggle={onToggleThinking}
          />
        )}

        {answer && (
          <div
            className={styles[`${cssClassName}__answer`]}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(answer),
            }}
          />
        )}

        {!message.isStreaming && (
          <>
            <div className={styles[`${cssClassName}__message-actions`]}>
              {/* {message.sources && message.sources.length > 0 && (
                <SourcesSection
                  sources={message.sources}
                  isExpanded={isSourcesExpanded}
                  onToggle={onToggleSources}
                />
              )} */}
              <div className={styles[`${cssClassName}__thumbs-container`]}>
                <button
                  className={styles[`${cssClassName}__thumbs`]}
                  onClick={handleSendNegativeFeedback}
                >
                  <img
                    src={
                      message.useful === false
                        ? thumbsDownRedIcon
                        : thumbsDownOutlineIcon
                    }
                    alt="Thumbs down"
                  />
                </button>
                <button
                  className={styles[`${cssClassName}__thumbs`]}
                  onClick={handleSendPositiveFeedback}
                >
                  <img
                    src={
                      message.useful === true
                        ? thumbsUpGreenIcon
                        : thumbsUpOutlineIcon
                    }
                    alt="Thumbs up"
                  />
                </button>
              </div>

              <div className={styles[`${cssClassName}__copy-button-wrapper`]}>
                <IconButton
                  fun={handleCopyAnswer}
                  icon={copyNodeIcon}
                  tooltip="Copy answer to clipboard"
                />
              </div>
            </div>
            {/* {isSourcesExpanded && (
                <ExpandedSourcesContainer message={message} />
              )} */}
          </>
        )}

        {message.isStreaming && thinking.length === 0 && !hasTools && (
          <div className={styles[`${cssClassName}__thinking`]}>
            <div className={styles[`${cssClassName}__spinner-wrapper`]}>
              <SmallSpinner />
            </div>
            <p>{texts.thinkingBubbleText}</p>
          </div>
        )}
      </>
    );
  };

  const renderUserMessage = () => {
    return (
      <>
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(message.content),
          }}
        />
        {message.images && message.images.length > 0 && (
          <div className={styles[`${cssClassName}__message-images`]}>
            {message.images.map((image, index) =>
              image.localFile ? (
                <LocalImage
                  key={index}
                  file={image.localFile}
                  className={styles[`${cssClassName}__message-image`]}
                  alt={image.interpretation}
                />
              ) : image.thumbnail ? (
                <img
                  key={index}
                  className={styles[`${cssClassName}__message-image`]}
                  src={`data:image/jpeg;base64,${image.thumbnail}`}
                  alt={image.interpretation}
                />
              ) : null,
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={`${styles[`${cssClassName}__message`]} ${
        styles[`${cssClassName}__message--${message.role}`]
      }`}
    >
      <div className={styles[`${cssClassName}__message-avatar`]}>
        {message.role === "user" ? (
          <img src={userIconBlue} alt="Human" />
        ) : (
          <img src={chatBotProfile} alt="Chat Bot" />
        )}
      </div>
      <div
        className={cn(
          styles[`${cssClassName}__message-content`],
          styles[`${cssClassName}__message-content--${message.role}`],
          message.createdAt
            ? styles[`${cssClassName}__message-content--with-timestamp`]
            : "",
        )}
      >
        <div>
          {message.role === "assistant"
            ? renderAssistantMessage()
            : renderUserMessage()}
        </div>
        {message?.createdAt && (
          <p className={styles[`${cssClassName}__timestamp`]}>
            <TimezoneDate date={message.createdAt} />
          </p>
        )}
      </div>
    </div>
  );
};
